import { get } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { INPUT_STATES } from '../../components/forms/forms.utils';
import SelectField from '../../components/forms/SelectField';
import Switch from '../../components/forms/Switch';
import TextArea from '../../components/forms/TextArea';
import TextField from '../../components/forms/TextField';
import { BRANCH_FIELDS, INITIAL_BRANCH_STATE, INITIAL_SCHOOL_STATE } from '../../constants/admin.schools.constants';
import {
  ADMIN_ORGANIZATIONS_PATHS,
  ADMIN_PATH,
  ADMIN_SCHOOLS_PATHS,
  ADMIN_TAB_PATHS
} from '../../constants/paths.constants';
import { FIELD_TYPES } from '../../constants/settings.constants';
import { useDependency } from '../../hooks/useDependency.hook';
import { useForm } from '../../hooks/useForm.hook';
import adminSchoolsService from '../../services/api/admin.schools.service';
import contactsService from '../../services/api/contacts.service';
import Modal from '../Modal';
import ModalTitle from '../ModalTitle';

const AddBranchModal = ({ isOpen, onClose }) => {
  const modalContentRef = useRef();
  const params = useParams();
  const navigate = useNavigate();
  const { data, isSubmitting, input, load, submitStart, submitOk, submitError, error } = useForm(INITIAL_BRANCH_STATE);
  const cityDistrictManagers = useDependency(contactsService.getCityContactsWithoutParents, {
    mapResponse: (items) =>
      items?.map((i) => {
        return {
          label: `${i?.spoContactData?.firstName} ${i?.spoContactData?.lastName}`,
          value: i?.id
        };
      })
  });

  const selectFieldsOptions = {
    cityDistrictManager: cityDistrictManagers.options
  };

  const handleConfirm = () => {
    submitStart();
    adminSchoolsService.addBranch(params.netId, params.managementId, params.communityId, params.schoolId, data, {
      onSuccess: (resp) => {
        submitOk();
        navigate(
          generatePath(
            `${ADMIN_PATH}${ADMIN_TAB_PATHS.ORGANIZATIONS.path}${ADMIN_ORGANIZATIONS_PATHS.SCHOOLS.path}${ADMIN_SCHOOLS_PATHS.AGODI_BRANCHES.path}`,
            {
              ...params,
              branchId: resp.id
            }
          )
        );
        handleClose();
      },
      onError: (message) => submitError(message)
    });
  };

  const handleClose = () => {
    load(INITIAL_SCHOOL_STATE);
    onClose();
  };

  useEffect(() => {
    if (!cityDistrictManagers.isLoading) {
      cityDistrictManagers.onLoad();
    }
  }, [cityDistrictManagers]);

  return (
    <Modal
      ref={modalContentRef}
      width={1200}
      className="spo-o-add-branch-modal"
      title={<ModalTitle hasError={!!error} title="Vestiging toevoegen" />}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      canConfirm={!!data?.name}
      isSubmitting={isSubmitting}
      onConfirmLabel="Toevoegen"
      onConfirmIcon="ai-check-1"
    >
      <div className="row">
        {BRANCH_FIELDS.filter((f) => f.field !== 'isVisible').map((field) => {
          const sharedProps = {
            label: field.label,
            value: data?.[field.field],
            onChange: (value) => input(value, field.field),
            disabled: isSubmitting,
            required: field.required
          };
          let FieldComponent = <TextField state={INPUT_STATES.DEFAULT} {...sharedProps} />;
          switch (field.type) {
            case FIELD_TYPES.SELECT:
              FieldComponent = (
                <SelectField
                  {...sharedProps}
                  parentElement={modalContentRef.current}
                  options={get(selectFieldsOptions, field.field, [])}
                />
              );
              break;
            case FIELD_TYPES.TEXT_AREA:
              FieldComponent = <TextArea state={INPUT_STATES.DEFAULT} {...sharedProps} />;
              break;
            case FIELD_TYPES.SWITCH:
              FieldComponent = (
                <Switch {...sharedProps} onClick={sharedProps.onChange} tip={field.tip} tipOffsetTop={96} />
              );
              break;

            default:
              break;
          }

          return (
            <div
              key={field.field}
              className={`${field.field === 'remarks' ? 'col-xs-12' : 'col-xs-6 col-lg-4'}  u-margin-bottom-sm`}
            >
              {FieldComponent}
            </div>
          );
        })}
      </div>
      {!!error && <p className="u-text-danger">{error}</p>}
    </Modal>
  );
};

export default AddBranchModal;
